import React, { useState, useEffect, useRef } from 'react';
import '../AdminDashboard/AdminDashboard.css';
import '../AdminDashboard/AdminDashboard-Isolated.css';
import collegeConfig from '../../config/collegeConfig';
import ManageTeachers from '../AdminDashboard/ManageTeachers';
import ManageStudents from '../AdminDashboard/ManageStudents';
import ManageNotice from '../AdminDashboard/ManageNotice';
import ManageStudentAssignments from '../AdminDashboard/ManageStudentAssignments';
import ManageAdditionalInfo from '../AdminDashboard/ManageAdditionalInfo';
import ManageThesisFaculty from '../AdminDashboard/ManageThesisFaculty';
import ManageBatchNames from '../AdminDashboard/ManageBatchNames';

import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaBell,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaUser,
  FaUsersCog,
  FaInfoCircle,
  FaGraduationCap,
  FaTags,
} from 'react-icons/fa';

const sidebarMenuItems = [
  {
    id: 'manage-teachers',
    title: 'Manage Teachers',
    icon: <FaChalkboardTeacher />,
    color: '#E74C3C'
  },
  {
    id: 'manage-students',
    title: 'Manage Students',
    icon: <FaUserGraduate />,
    color: '#4A90E2'
  },
  {
    id: 'manage-student-assignments',
    title: 'Student Assignments',
    icon: <FaUsersCog />,
    color: '#2c8e6eff'
  },
  {
    id: 'manage-notice',
    title: 'Manage Notice',
    icon: <FaBell />,
    color: '#0099ffff'
  },
  {
    id: 'manage-additional-info',
    title: 'Additional Info',
    icon: <FaInfoCircle />,
    color: '#9B59B6'
  },
  {
    id: 'manage-thesis-faculty',
    title: 'Thesis Faculty',
    icon: <FaGraduationCap />,
    color: '#7c3aed'
  },
  {
    id: 'manage-batch-names',
    title: 'Batch Names',
    icon: <FaTags />,
    color: '#0d9488'
  }
];

const CollegeAdminDashboard = ({ onNavigate }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('manage-teachers');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [user, setUser] = useState(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showProfileMenu]);

  // Get user data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!token || !userData) {
      if (onNavigate) {
        onNavigate('login');
      }
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    } catch (error) {
      console.error('Error parsing user data:', error);
      if (onNavigate) {
        onNavigate('login');
      }
    }
  }, [onNavigate]);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  const handleMenuClick = (menuId) => {
    setActiveMenu(menuId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'manage-teachers':
        return <ManageTeachers />;

      case 'manage-students':
        return <ManageStudents />;

      case 'manage-student-assignments':
        return <ManageStudentAssignments />;

      case 'manage-notice':
        return <ManageNotice />;

      case 'manage-additional-info':
        return <ManageAdditionalInfo />;

      case 'manage-thesis-faculty':
        return <ManageThesisFaculty />;

      case 'manage-batch-names':
        return <ManageBatchNames />;

      default:
        return null;
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="admin-dashboard-container">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img
              src={collegeConfig.logo}
              alt={`${collegeConfig.shortName} Logo`}
              className="sidebar-logo"
            />
            <div className="logo-text">
              <h2>{collegeConfig.shortName}</h2>
              <p>{collegeConfig.appName}</p>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {sidebarMenuItems.map((item) => (
            <div
              key={item.id}
              className={`sidebar-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="sidebar-icon" style={{ color: item.color }}>
                {item.icon}
              </span>
              <span className="sidebar-text">{item.title}</span>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-item logout-item" onClick={handleLogout}>
            <span className="sidebar-icon">
              <FaSignOutAlt />
            </span>
            <span className="sidebar-text">Logout</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Header */}
        <header className="admin-header">
          <div className="header-left">
            <button className="sidebar-toggle" onClick={toggleSidebar}>
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
            <h2 className="page-title">
              {sidebarMenuItems.find(item => item.id === activeMenu)?.title || 'Dashboard'}
            </h2>
          </div>

          <div className="header-right">
            <div className="profile-section" onClick={() => setShowProfileMenu(!showProfileMenu)}>
              <div className="profile-avatar">
                <FaUser />
              </div>
              <div className="profile-info">
                <span className="profile-name">{user?.name || 'College Admin'}</span>
                <span className="profile-role">College Admin</span>
              </div>
              <span className="dropdown-arrow">▼</span>

              {showProfileMenu && (
                <div className="profile-dropdown">
                  <div className="dropdown-item logout" onClick={handleLogout}>
                    <span className="dropdown-icon">🚪</span>
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="page-content">
          {renderContent()}
        </div>

        {/* Footer */}
        <footer className="admin-footer">
          <p>© {collegeConfig.copyrightYear} {collegeConfig.collegeName} | College Admin Dashboard v{collegeConfig.version}</p>
        </footer>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>
      )}
    </div>
  );
};

export default CollegeAdminDashboard;
